import AccountTreeIcon from '@mui/icons-material/AccountTree'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ImageIcon from '@mui/icons-material/Image'
import { Box } from '@mui/material'
import { MenuItemLinkWrapper } from 'components/common/MenuItemLinkWrapper'
import { useState } from 'react'
import { DashboardMenuItem, MenuProps, useSidebarState, useTranslate } from 'react-admin'
import packageGroup from '../resources/packageGroup'
import collections from '../resources/packageGroupCollection'
import packages from '../resources/packages'
import personalization from '../resources/personalizationRule'
import presets from '../resources/presets'
import retailerRepresentative from '../resources/retailerRepresentative'
import retailers from '../resources/retailers'
import retailerTag from '../resources/retailerTag'
import SubMenu from './SubMenu'

type MenuName = 'menuPackages' | 'menuRetailers' | 'menuModels'

const Menu = ({ dense = false }: MenuProps) => {
    const translate = useTranslate()
    const [open] = useSidebarState()
    const [state, setState] = useState({
        menuPackages: false,
        menuModels: false,
        menuRetailers: false
    })
    const handleToggle = (menu: MenuName) => {
        setState((state) => ({ ...state, [menu]: !state[menu] }))
    }

    return (
        <Box
            component="div"
            sx={{
                marginTop: 1,
                marginBottom: 1,
                // When sidebar is closed (!open), center align all icons
                ...(!open && {
                    '& .MuiMenuItem-root': {
                        paddingLeft: '16px'
                    },

                    'li.MuiMenuItem-root': {
                        display: 'none'
                    }
                })
            }}
        >
            <DashboardMenuItem />
            <SubMenu
                handleToggleCallback={() => handleToggle('menuPackages')}
                isOpen={state.menuPackages || !open}
                name={translate('manager.menu.package_manager')}
                icon={<ChevronRightIcon />}
                dense={dense}
            >
                <MenuItemLinkWrapper
                    name="Package"
                    to={{
                        pathname: '/packageGroupCollection',
                        state: { _scrollToTop: true }
                    }}
                    primaryText={translate(`manager.menu.collections`)}
                    leftIcon={<collections.icon />}
                    dense={dense}
                />
                <MenuItemLinkWrapper
                    name={'PackageGroup'}
                    to={{
                        pathname: '/packageGroup',
                        state: { _scrollToTop: true }
                    }}
                    primaryText={translate(`manager.menu.packageGroup`)}
                    leftIcon={<packageGroup.icon />}
                    dense={dense}
                />
                <MenuItemLinkWrapper
                    name={'PackageGroupCollection'}
                    to={{
                        pathname: '/Package',
                        state: { _scrollToTop: true }
                    }}
                    primaryText={translate(`manager.menu.packages`)}
                    leftIcon={<packages.icon />}
                    dense={dense}
                />
            </SubMenu>
            <SubMenu
                handleToggleCallback={() => handleToggle('menuRetailers')}
                isOpen={state.menuRetailers || !open}
                name={translate('manager.menu.retailer_manager')}
                icon={<ChevronRightIcon />}
                dense={dense}
            >
                <MenuItemLinkWrapper
                    name={'Retailer'}
                    to={{
                        pathname: '/retailer',
                        state: { _scrollToTop: true }
                    }}
                    primaryText={translate(`manager.menu.retailers`)}
                    leftIcon={<retailers.icon />}
                    dense={dense}
                />
                <MenuItemLinkWrapper
                    name={'Retailer'}
                    to={{
                        pathname: '/retailerRepresentative',
                        state: { _scrollToTop: true }
                    }}
                    primaryText={translate(`manager.menu.contactPersons`)}
                    leftIcon={<retailerRepresentative.icon />}
                    dense={dense}
                />
                {/*<MenuItemLinkWrapper*/}
                {/*    name={'RetailerYear'}*/}
                {/*    to={{*/}
                {/*        pathname: '/retailerYear',*/}
                {/*        state: { _scrollToTop: true }*/}
                {/*    }}*/}
                {/*    primaryText={translate(`manager.menu.years`)}*/}
                {/*    leftIcon={<retailerYear.icon />}*/}
                {/*    dense={dense}*/}
                {/*/>*/}
                {/*<MenuItemLinkWrapper*/}
                {/*    name={'Education'}*/}
                {/*    to={{*/}
                {/*        pathname: '/education',*/}
                {/*        state: { _scrollToTop: true }*/}
                {/*    }}*/}
                {/*    primaryText={translate(`manager.menu.educations`)}*/}
                {/*    leftIcon={<education.icon />}*/}
                {/*    dense={dense}*/}
                {/*/>*/}
                <MenuItemLinkWrapper
                    name={'RetailerTag'}
                    to={{
                        pathname: '/retailerTag',
                        state: { _scrollToTop: true }
                    }}
                    primaryText={translate(`manager.menu.tags`)}
                    leftIcon={<retailerTag.icon />}
                    dense={dense}
                />
            </SubMenu>
            <SubMenu
                handleToggleCallback={() => handleToggle('menuModels')}
                isOpen={state.menuModels || !open}
                name={translate('manager.menu.model_manager')}
                icon={<ChevronRightIcon />}
                dense={dense}
            >
                <MenuItemLinkWrapper
                    name={'Model'}
                    to={{
                        pathname: '/models',
                        state: { _scrollToTop: true }
                    }}
                    primaryText={translate(`manager.menu.model`)}
                    leftIcon={<AccountTreeIcon />}
                    dense={dense}
                />
                <MenuItemLinkWrapper
                    name={'Preset'}
                    to={{
                        pathname: '/preset',
                        state: { _scrollToTop: true }
                    }}
                    primaryText={translate(`manager.menu.presets`)}
                    leftIcon={<presets.icon />}
                    dense={dense}
                />
            </SubMenu>
            <MenuItemLinkWrapper
                name={'Rule'}
                to={{
                    pathname: '/rule',
                    state: { _scrollToTop: true }
                }}
                primaryText={translate(`manager.menu.personalization`)}
                leftIcon={<personalization.icon />}
                dense={dense}
            />
            <MenuItemLinkWrapper
                name={'Gallery'}
                to={{
                    pathname: '/gallery',
                    state: { _scrollToTop: true }
                }}
                primaryText={translate(`manager.menu.gallery`)}
                leftIcon={<ImageIcon />}
                dense={dense}
            />

            {/* Logo at bottom of sidebar */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 16,
                    left: open ? 16 : 4,
                    width: !open ? '48px' : 'calc(220px - 32px)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    transition: 'left 0.2s ease-in-out, width 0.2s ease-in-out'
                }}
            >
                <img
                    src="/proud-nerds-logo.png"
                    alt="Proud Nerds Logo"
                    style={{
                        maxWidth: '100%',
                        height: 'auto',
                        maxHeight: !open ? '32px' : '60px',
                        transition: 'max-height 0.2s ease-in-out'
                    }}
                />
            </Box>
        </Box>
    )
}

export default Menu
